import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import { useBreakpoint } from '@/shared/lib/hooks';
import { Button } from './Button';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: string;
}

/**
 * A side panel on tablet/desktop, a bottom sheet on phones. Used for the editor inspector at
 * tablet width and for filter panels.
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'w-[22rem]',
}: DrawerProps) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-black/50" />
        <RadixDialog.Content
          className={cn(
            'fixed z-50 flex flex-col border-border bg-surface shadow-lg focus:outline-none',
            isMobile
              ? 'inset-x-0 bottom-0 max-h-[88vh] animate-slide-in-bottom rounded-t-xl border-t'
              : cn('inset-y-0 right-0 animate-slide-in-right border-l', width),
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3">
            <div className="min-w-0">
              <RadixDialog.Title className="text-base font-semibold">{title}</RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="mt-0.5 text-xs text-fg-muted">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label={t('a11y.closeDialog')}>
                <X className="h-4 w-4" />
              </Button>
            </RadixDialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

          {footer && (
            <div className="shrink-0 border-t border-border px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
